<template>
    <div class="transaction-container" @click="handleClick" v-if="!(transaction.type === 'gift' && !isVoucherGiftActive)">
        <div class="transaction-item">
            <!-- Image Transaction -->
            <div class="transaction-item-image">
                <img :src="transactionImage.src" :style="transactionImageStyle" alt="">
            </div>

            <!-- Detail Transaction -->
            <div class="transaction-item-detail">
                <h1 class="transaction-title" :class="{ 'text-gift': transaction.type === 'gift' }">
                    {{ getTransactionTitle() }}
                </h1>
                <p class="transaction-description">
                    <template v-if="transaction.merchant_id && (transaction.merchant_name || transaction.branch_name)">
                        {{ transaction.branch_name }} • {{ transaction.merchant_name }}
                    </template>
                    <template v-else>
                        {{ transaction.corporate_name }}
                    </template>
                </p>
                <div class="transaction-date">
                    {{ formatTransactionDate() }}
                </div>
            </div>
        </div>

        <div v-if="transaction.type === 'gift'" class="transaction-points points-gift">
            <span>🎁 Gift</span>
        </div>
        <div v-else-if="isPointSystemActive" class="transaction-points" :class="getPointsClass()">
            <span>{{ formatPoints() }}</span>
        </div>
    </div>
</template>

<script>
import { usePointSystem } from '@/composables/pointsystem';
import { computed } from 'vue';
import { useStore } from 'vuex';

export default {
    name: 'MyTransaction',
    emits: ['open-receipt'],
    props: {
        transaction: {
            type: Object,
            required: true,
            default: () => ({})
        },
        vouchers: {
            type: Array,
            default: () => []
        }
    },
    setup() {
        const store = useStore();
        const { isPointSystemActive } = usePointSystem();
        const isVoucherGiftActive = computed(() => store.getters['FeatureFlag/getVoucherGiftFlag'] !== 'NONACTIVE');
        return {
            isPointSystemActive,
            isVoucherGiftActive
        };
    },
    computed: {
        membershipImages() {
            return [
                '/img/member-img/mem-product-1.svg',
                '/img/member-img/mem-product-2.svg',
            ];
        },
        bookingEngineImages() {
            return [
                '/img/member-img/be-product-1.svg',
                '/img/member-img/be-product-2.svg',
                '/img/member-img/be-product-3.svg'
            ];
        },
        transactionImage() {
            const imageSrc = this.getTransactionImage();
            const isMembershipStock = typeof imageSrc === 'string' && (imageSrc.includes('/img/member-img/mem-') || imageSrc.includes('expired-transaction-icon.svg'));
            const isExpiredIcon = typeof imageSrc === 'string' && imageSrc.includes('expired-transaction-icon.svg');
            
            return {
                src: imageSrc,
                isMembershipStock,
                isExpiredIcon
            };
        },
        transactionImageStyle() {
            const isMembershipStock = this.transactionImage.isMembershipStock;
            const isExpiredIcon = this.transactionImage.isExpiredIcon;
            
            return {
                objectFit: isMembershipStock ? 'scale-down' : 'cover',
                padding: isExpiredIcon ? '8px' : '0'
            };
        }
    },
    methods: {
        handleClick() {
            this.$emit('open-receipt', this.transaction);
        },

        getTransactionImage() {
            const getRandomFallback = () => {
                const fallbacks = this.bookingEngineImages;
                return fallbacks[Math.floor(Math.random() * fallbacks.length)];
            };

            if (!this.transaction) {
                return getRandomFallback();
            }

            const checkType = (this.transaction.type || '').toLowerCase();
            const checkDetail = (this.transaction.detail_name || '').toLowerCase();
            const checkCode = (this.transaction.transaction_code || this.transaction.code || '');

            if (checkType === 'expiration' || checkDetail.includes('expired') || checkDetail.includes('expiration') || checkCode.startsWith('EXP-')) {
                return '/img/member-icon/expired-transaction-icon.svg';
            }

            const voucherImage = this.transaction?.voucher?.avatar?.image_url
                || this.transaction?.voucher?.image_url
                || this.transaction?.voucher_avatar
                || this.transaction?.voucher_avatar_url
                || this.transaction?.avatar?.image_url;
            if (voucherImage) {
                return voucherImage;
            }

            const resolvedVoucherImage = this.getVoucherImageFromList();
            if (resolvedVoucherImage) {
                return resolvedVoucherImage;
            }

            const voucherId = this.transaction?.voucher_id
                || this.transaction?.voucherId
                || this.transaction?.voucher?.id;

            if (voucherId) {
                const s3Bucket = process.env.VUE_APP_S3_BUCKET || 'https://s3.ap-southeast-1.amazonaws.com/cdn-dev-membership.omnihotelier.net';
                // Try to use direct S3 URL as fallback if image lookup failed
                return `${s3Bucket}/user_assets/${voucherId}.jpeg`;
            }

            const type = (this.transaction.type || '').toLowerCase();
            const detail = (this.transaction.detail_name || '').toLowerCase();

            const getRandom = (list) => {
                if (!list.length) return getRandomFallback();
                const index = Math.floor(Math.random() * list.length);
                return list[index];
            };

            if (type === 'gift') {
                if (voucherImage) return voucherImage;
                if (resolvedVoucherImage) return resolvedVoucherImage;
                return getRandom(this.membershipImages);
            }

            if (type === 'redeem' || detail.includes('redeem')) {
                if (voucherImage) {
                    return voucherImage;
                }
                if (resolvedVoucherImage) {
                    return resolvedVoucherImage;
                }
                return getRandom(this.membershipImages);
            }

            if (detail.includes('membership')) {
                return getRandom(this.membershipImages);
            }

            if (detail.includes('booking')) {
                return getRandom(this.bookingEngineImages);
            }

            if (type === 'transaction') {
                return getRandom(this.membershipImages);
            }

            return getRandomFallback();
        },

        getVoucherImageFromList() {
            // Debugging Transaction Image Resolution
            // console.log('DEBUG TR IMG:', this.transaction.detail_name, this.transaction.voucher_id, 'Vouchers:', this.vouchers ? this.vouchers.length : 0);

            if (!Array.isArray(this.vouchers) || !this.vouchers.length) {
                return null;
            }

            const extractImage = (voucher) => {
                return voucher?.avatar?.image_url
                    || voucher?.avatar?.image
                    || voucher?.image_url
                    || voucher?.background_image
                    || null;
            };

            const voucherId = this.transaction?.voucher_id
                || this.transaction?.voucherId
                || this.transaction?.voucher?.id;

            if (voucherId != null) {
                const matchedById = this.vouchers.find(voucher => voucher?.id === voucherId);
                const imageById = extractImage(matchedById);
                if (imageById) {
                    return imageById;
                }
            }

            const detailRaw = this.transaction?.detail_name || '';
            const detailParts = detailRaw
                .split('$')
                .map(part => part.trim())
                .filter(Boolean);

            for (const part of detailParts) {
                const matchedByName = this.vouchers.find(voucher => (voucher?.name || '').trim().toLowerCase() === part.toLowerCase());
                const imageByName = extractImage(matchedByName);
                if (imageByName) {
                    return imageByName;
                }
            }

            return null;
        },

        getTransactionTitle() {
            if (!this.transaction) return 'Transaction';

            const typeRaw = this.transaction.type || '';
            const type = typeRaw ? typeRaw.charAt(0).toUpperCase() + typeRaw.slice(1) : '';
            const detailRaw = this.transaction.detail_name;

            // Handle gift type specifically
            if (typeRaw === 'gift') {
                return detailRaw ? `Gift - ${detailRaw}` : 'Gift Voucher';
            }

            const checkDetail = (detailRaw || '').toLowerCase();
            const checkCode = (this.transaction.transaction_code || this.transaction.code || '');
            if (typeRaw === 'expiration' || checkDetail.includes('expired') || checkDetail.includes('expiration') || checkCode.startsWith('EXP-')) {
                if (detailRaw === 'Point Expiration Deduction' || detailRaw === 'Deduction Point' || !detailRaw) {
                    return 'Expiration - Deduction Point';
                }
                return `Expiration - ${detailRaw}`;
            }

            if (detailRaw === 'Top Up') {
                return detailRaw;
            }

            if (detailRaw === 'Deduction Point' || detailRaw == null) {
                return 'Deduction Point';
            }

            const detailParts = (detailRaw || '')
                .split('$')
                .map(part => part.trim())
                .filter(Boolean);

            if (!detailParts.length) {
                return type ? `${type} - Deduction Point` : 'Deduction Point';
            }

            const [firstDetail, ...restDetails] = detailParts;
            const extraCount = restDetails.length;
            const detailText = extraCount ? `${firstDetail} +${extraCount}` : firstDetail;

            return type ? `${type} - ${detailText}` : detailText;
        },
        
        formatTransactionDate() {
            if (!this.transaction || !this.transaction.created_at) {
                return 'Oct 02, 2025 • 02.30 PM';
            }

            const date = this.transaction.created_at;
            const datePart = date.slice(0, 10).split('-');
            const timePart = date.slice(11, 16).split(':');

            const newDate = new Date(
                parseInt(datePart[0]),
                parseInt(datePart[1], 10) - 1,
                parseInt(datePart[2], 10),
                parseInt(timePart[0], 10),
                parseInt(timePart[1], 10)
            );

            if (Number.isNaN(newDate.getTime())) {
                return 'Oct 02, 2025 • 02.30 PM';
            }

            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[newDate.getMonth()];
            const day = String(newDate.getDate()).padStart(2, '0');
            const year = newDate.getFullYear();

            let hours = newDate.getHours();
            const minutes = String(newDate.getMinutes()).padStart(2, '0');
            const period = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            const hoursDisplay = String(hours).padStart(2, '0');

            return `${month} ${day}, ${year} • ${hoursDisplay}.${minutes} ${period}`;
        },
        
        formatPoints() {
            if (!this.transaction) return '-1 POIN';
            
            const point = this.transaction.point !== null && this.transaction.point !== undefined 
                ? this.transaction.point 
                : this.transaction.point_deducted;
                
            if (point === undefined || point === null) return '0 POIN';
            
            const formattedValue = Math.abs(point).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
            const detailNameLower = (this.transaction.detail_name || '').toLowerCase();
            const type = (this.transaction.type || '').toLowerCase();
            const txCode = (this.transaction.transaction_code || this.transaction.code || '');
            
            // Logika untuk menentukan tanda + atau -
            if (this.transaction.is_pending === 1) {
                return `${formattedValue} POIN`;
            }
            
            // Expiration is always minus
            if (type === 'expiration' || detailNameLower.includes('expired') || detailNameLower.includes('expiration') || txCode.startsWith('EXP-')) {
                return `-${formattedValue} POIN`;
            }
            
            if (
                (type === 'transaction' && !detailNameLower.includes('reedem') && !detailNameLower.includes('deduction') && point >= 0) ||
                type === 'cancel'
            ) {
                return `+${formattedValue} POIN`;
            }
            
            if (
                type === 'redeem' ||
                (type === 'transaction' && (detailNameLower.includes('reedem') || detailNameLower.includes('deduction') || point < 0))
            ) {
                return `-${formattedValue} POIN`;
            }
            
            return `${formattedValue} POIN`;
        },
        
        getPointsClass() {
            if (!this.transaction) return '';
            
            const detailNameLower = (this.transaction.detail_name || '').toLowerCase();
            const type = (this.transaction.type || '').toLowerCase();
            const point = this.transaction.point !== null && this.transaction.point !== undefined 
                ? this.transaction.point 
                : this.transaction.point_deducted;

            // Jika pending, gunakan class default
            if (this.transaction.is_pending === 1) {
                return 'points-pending';
            }
            
            const txCode = (this.transaction.transaction_code || this.transaction.code || '');

            // Expiration
            if (type === 'expiration' || detailNameLower.includes('expired') || detailNameLower.includes('expiration') || txCode.startsWith('EXP-')) {
                return 'points-minus';
            }
            
            // Jika transaksi menambah poin (plus), gunakan warna hijau
            if (
                (type === 'transaction' && !detailNameLower.includes('reedem') && !detailNameLower.includes('deduction') && point >= 0) ||
                type === 'cancel'
            ) {
                return 'points-plus';
            }
            
            // Jika transaksi mengurangi poin (spend), gunakan warna default (merah)
            if (
                type === 'redeem' ||
                (type === 'transaction' && (detailNameLower.includes('reedem') || detailNameLower.includes('deduction') || point < 0))
            ) {
                return 'points-minus';
            }
            
            return '';
        }
    }
}
</script>

<style scoped>
.transaction-title {
    font-size: 14px;
    font-weight: bold;
    color: #111827;
    line-height: 1.3;
    margin-bottom: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.transaction-description {
    font-size: 12px;
    font-weight: normal;
    color: #111827;
    line-height: 1.3;
    margin-bottom: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.transaction-date {
    font-size: 12px;
    font-weight: normal;
    color: #99A0AE;
    line-height: 100%;
    white-space: nowrap;
}

.transaction-item-image {
    width: 44px;
    min-width: 44px;
    height: 44px;
    min-height: 44px;
    background-color: white;
    border-radius: 50%;
    border: 1px solid #E1E4EA;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.transaction-item-image img {
    margin: auto;
    width: 100%;
    height: 100%;
}

.transaction-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background-color: white;
    border-radius: 12px;
    gap: 12px;
    padding: 16px;
    cursor: pointer;
}

.transaction-item {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0; /* Prevent overflow */
}

.transaction-item-detail {
    flex: 1;
    min-width: 0;
}

.transaction-points {
    font-size: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    height: fit-content;
    padding: 2px 8px 2px 8px;
    background-color: var(--secondary-color, #FFE6E6);
    color: var(--primary-color, #D92D20);
    align-self: flex-start; /* Position at top right */
    margin-top: 0;
    flex-shrink: 0; /* Prevent shrinking */
}

.transaction-points span {
    white-space: nowrap;
}

/* Class untuk poin plus (hijau) */
.transaction-points.points-plus {
    background-color: #E6F7E6;
    color: #00AA00;
}

/* Class untuk poin minus (default - merah) */
.transaction-points.points-minus {
    background-color: #FFE6E6;
    color: #D92D20;
}

/* Class untuk poin pending (abu-abu) */
.transaction-points.points-pending {
    background-color: #F5F5F5;
    color: #6B7280;
}

/* Class untuk gift voucher (hijau - seperti reward) */
.transaction-points.points-gift {
    background-color: #D1FAE5;
    color: #065F46;
}


</style>
